import { useCallback, useState } from 'react';
import { ColumnVisibilityModels } from '../../../../../../components/Table/ColumnVisibilityModels';
import { Items } from '../../../../../../types';
import { buildFiltersObject } from '../../../helpers/buildFiltersObject';
import { fetchDownloadData } from '../../../apiCalls';
import { getActiveColumnFilters } from '../../../utils/activeColumnFilters';
import {
  getJoinKeyUiColumns,
  matchEvidenceToSummary,
  SummaryMatchStats,
  withForcedDownloadColumns,
} from '../../../utils/matchEvidenceToSummary';
import { MappingContent, SearchItem } from '../types';

type UseSummaryMatchParams = {
  view: string;
  source: string;
  tab: string;
  searchItems: SearchItem[];
  isAuthenticated: boolean;
  authorization: unknown;
  isMapping?: boolean;
  mappingContent?: MappingContent;
  sourceWeights?: Array<{ name: string; score: number }>;
  evidenceColumnVisibilityModel?: Record<string, boolean>;
  summaryColumnVisibilityModel?: Record<string, boolean>;
  getFilterValueRef: React.MutableRefObject<(() => Array<{ name: string; value: unknown }>) | null>;
  dispatch: (action: { type: string; payload?: boolean }) => void;
  onWarning: (message: string) => void;
  onError: (message: string) => void;
};

function toApiSearchItems(searchItems: SearchItem[]): Items[] {
  return searchItems.map(item => ({
    id: item.id,
    name: item.name,
    [item.id]: item.name,
  }));
}

export function useSummaryMatch({
  view,
  source,
  tab,
  searchItems,
  isAuthenticated,
  authorization,
  isMapping = false,
  mappingContent,
  sourceWeights = [],
  evidenceColumnVisibilityModel,
  summaryColumnVisibilityModel,
  getFilterValueRef,
  dispatch,
  onWarning,
  onError,
}: UseSummaryMatchParams) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matchedRows, setMatchedRows] = useState<Record<string, string>[]>([]);
  const [stats, setStats] = useState<SummaryMatchStats | null>(null);

  const openSummaryMatch = useCallback(async () => {
    const rawFilters = getFilterValueRef.current?.() ?? [];
    const activeFilters = getActiveColumnFilters(rawFilters);

    if (activeFilters.length === 0) {
      onError('Apply at least one filter to view matching Summary associations.');
      return;
    }

    if (searchItems.length > 50) {
      onError('Cannot process more than 50 search items. Please reduce your selection.');
      return;
    }

    const joinKeys = getJoinKeyUiColumns(tab);
    const evidenceDownloadModel = withForcedDownloadColumns(evidenceColumnVisibilityModel, joinKeys);
    const resolvedSummaryModel =
      summaryColumnVisibilityModel ?? ColumnVisibilityModels.getByViewTab(view, tab, false) ?? {};
    const summaryDownloadModel = withForcedDownloadColumns(resolvedSummaryModel, joinKeys);

    const apiSearchItems = toApiSearchItems(searchItems);
    const apiMappingContent = mappingContent
      ? {
          isActive: mappingContent.isActive,
          columVisibilityModelKey: mappingContent.columVisibilityModelKey,
          mappingItems: mappingContent.mappingItems?.map(item => ({
            filterParam: null,
            filterValue: item.id,
          })),
        }
      : undefined;

    setIsOpen(true);
    setIsLoading(true);
    setError(null);
    setMatchedRows([]);
    setStats(null);
    onWarning('Building Summary match from filtered Evidence…');
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const [evidenceRows, summaryRows] = await Promise.all([
        fetchDownloadData({
          columnVisibilityModel: evidenceDownloadModel,
          view,
          source,
          tab,
          searchItems: apiSearchItems,
          isAuthenticated,
          authorization,
          isEvidence: true,
          isMapping,
          mappingContent: apiMappingContent,
          filters: buildFiltersObject(activeFilters as Parameters<typeof buildFiltersObject>[0]),
          sourceWeights,
        }),
        fetchDownloadData({
          columnVisibilityModel: summaryDownloadModel,
          view,
          source,
          tab,
          searchItems: apiSearchItems,
          isAuthenticated,
          authorization,
          isEvidence: false,
          isMapping,
          mappingContent: apiMappingContent,
          filters: {},
          sourceWeights,
        }),
      ]);

      const { matchedRows: rows, stats: matchStats } = matchEvidenceToSummary(evidenceRows, summaryRows, tab);

      if (rows.length === 0) {
        setError('No Summary associations match the current filtered Evidence. Try adjusting your filters.');
      }

      setMatchedRows(rows);
      setStats(matchStats);
    } catch (err) {
      console.error('Summary match failed:', err);
      setError('Could not build the Summary match. Please try again.');
      onError('Could not build the Summary match.');
    } finally {
      setIsLoading(false);
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [
    authorization,
    dispatch,
    evidenceColumnVisibilityModel,
    getFilterValueRef,
    isAuthenticated,
    isMapping,
    mappingContent,
    onError,
    onWarning,
    searchItems,
    source,
    sourceWeights,
    summaryColumnVisibilityModel,
    tab,
    view,
  ]);

  const closeSummaryMatch = useCallback(() => {
    setIsOpen(false);
    setError(null);
  }, []);

  return {
    isOpen,
    isLoading,
    error,
    matchedRows,
    stats,
    openSummaryMatch,
    closeSummaryMatch,
  };
}
